//Built-in libraries

//Third party libraries
import { PlusIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { NumericFormat as NumberFormat } from 'react-number-format';
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import InputError from '@/components/UI/Form/Error';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Confirm from '@/components/UI/Modal/Confirm';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';

//STP Libraries

import RadioGroupInput from '@/components/UI/Form/RadioGroup';
import type { ReactSelectOption } from '@/types/Constants';
//Assets and Miscellaneous
import { initialValues, JobClassifications } from './constants';
import type { IJobClassification } from './types';

//Component Begin

const JobClassification = ({ formValues }: { formValues: any }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editJobId, setEditJobId] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<IJobClassification>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [job, setJob] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setJob(initialValues);
  };

  const editJobHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(job).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...job[next],
          value: f[next],
          isTouched: !!f[next],
        },
      };
    }, {});
    setJob(formData);
    setEditJobId(f.id);
  };

  const updateJobHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(job).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: job[next].value,
            id: editJobId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step4: {
          ...data.step4,
          section1: {
            ...data.step4.section1,
            jobClassification: data.step4.section1.jobClassification.map((p: any) => {
              if (p.id === editJobId) {
                return formattedData;
              }
              return p;
            }),
          },
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeJobHandler = (job: IJobClassification) => {
    setShowRemoveConfirm(true);
    setSelectedJob(job);
  };

  const confirmRemoveJob = async () => {
    setShowRemoveConfirm(false);
    const filteredJob = data.step4.section1.jobClassification.filter((job: any) => job.id !== selectedJob?.id);
    updateData({
      ...data,
      step4: {
        ...data.step4,
        section1: {
          ...data.step4.section1,
          jobClassification: filteredJob,
        },
      },
    });

    if (selectedJob?.id === editJobId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveJob = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...job };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setJob((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddJobHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(job).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: job[next].value };
        },
        {}
      );
      updateData({
        ...data,
        step4: {
          ...data.step4,
          section1: {
            ...data.step4.section1,
            jobClassification: [...data.step4.section1.jobClassification, { ...formattedData, id: uuidv4() }],
          },
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = createInputChangedHandler(setJob, job);

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Job Classification'
          message={`You are about to remove ${selectedJob?.title} job. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveJob}
          decline={cancelRemoveJob}
        />
      )}
      <section className='space-y-4 col-span-6'>
        {data.step4?.section1.jobClassification.length > 0 && (
          <div className='space-y-2 w-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Job Classifications</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>How Many?</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Job Type</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>Employment Duration</th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step4.section1.jobClassification.map((job: IJobClassification) => (
                  <tr key={job.id} className={`${job.id === editJobId ? 'bg-neutralColor-lighter' : ''}`}>
                    <td className='p-4'>{job?.title}</td>
                    <td className='p-4'>{job?.noOfJobs}</td>
                    <td className='p-4'>{job?.jobType}</td>
                    <td className='p-4'>{job?.employmentDuration}</td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div className='cursor-pointer hover:text-blue-500' onClick={() => editJobHandler(job)}>
                        Edit
                      </div>
                      <div className='cursor-pointer hover:text-tertiaryColor-normal' onClick={() => removeJobHandler(job)}>
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <ReactSelect
              label='Job Classifications'
              id='jobClassifications'
              name='title'
              options={JobClassifications.sort((a, b) => {
                const titleA = a.title.toLowerCase();
                const titleB = b.title.toLowerCase();

                if (titleA < titleB) {
                  return -1;
                }
                if (titleA > titleB) {
                  return 1;
                }
                return 0;
              }).map((job) => ({
                label: job.title,
                value: job.title,
              }))}
              value={{
                label: job.title.value,
                value: job.title.value,
              }}
              error={job?.title?.error}
              onChange={(option) => {
                inputChangedHandler((option as ReactSelectOption).value, 'title');
              }}
              onBlur={(_e) => {
                inputChangedHandler(job.title.value, 'title');
              }}
            />
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label className={'leading-7 block text-sm font-medium text-neutralColor-medium'} htmlFor='noOfJobs'>
              How Many?
            </label>
            <NumberFormat
              name='noOfJobs'
              id='noOfJobs'
              value={job.noOfJobs.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-hidden'
              thousandSeparator={true}
              valueIsNumericString
              maxLength={21}
              onValueChange={(target, _s) => {
                inputChangedHandler(target.floatValue, 'noOfJobs');
              }}
              onBlur={(e: any) => {
                inputChangedHandler(job.noOfJobs.value, e.target.name);
              }}
            />
            {job?.noOfJobs?.error && <InputError>{job?.noOfJobs?.error}</InputError>}
          </div>
        </div>
        <div className='w-full grid grid-cols-6 gap-4'>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <RadioGroupInput
              value={job.jobType.value ?? ''}
              onChange={(value) => {
                inputChangedHandler(value, 'jobType');
              }}
              label='Job Type'
              name='jobType'
              id='job-type'
              options={[
                { label: 'Full-time', value: 'Full-time' },
                { label: 'Part-time', value: 'Part-time' },
              ]}
            />
            {job?.jobType?.error && <InputError>{job?.jobType?.error}</InputError>}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <RadioGroupInput
              value={job.employmentDuration.value ?? ''}
              onChange={(value) => {
                inputChangedHandler(value, 'employmentDuration');
              }}
              label='Employment Duration'
              name='employmentDuration'
              id='employment-duration'
              options={[
                { label: 'Temporary', value: 'Temporary' },
                { label: 'Permanent', value: 'Permanent' },
              ]}
            />
            {job?.employmentDuration?.error && <InputError>{job?.employmentDuration?.error}</InputError>}
          </div>
        </div>

        {!isEditing && <Button icon={<PlusIcon />} label='Add' type='button' btnStyle='primary' onClick={onAddJobHandler} />}
        {isEditing && (
          <div className='flex gap-4'>
            <Button label='Save' type='button' btnStyle='primary' onClick={updateJobHandler} />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditJobId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default JobClassification;
